import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Fade from '@mui/material/Fade';
import Typography from '@mui/material/Typography';
import { Field, FieldProps, Form, Formik } from 'formik';
import React from 'react';
import * as Yup from 'yup';
import { RouterLink } from '../../../../components/Link';
import { TextField } from '../../../../components/TextField';
import type { OnSubmit } from '../../../../design-system';
import { createSchema } from '../../../../utils/formUtils';

type ForgotPasswordSubmit = { newPassword: string; confirmNewPassword: string };

const initialValues: ForgotPasswordSubmit = { newPassword: '', confirmNewPassword: '' };

export const newPasswordSchema = createSchema<ForgotPasswordSubmit>({
  newPassword: Yup.string()
    .required('New password is required')
    // eslint-disable-next-line no-template-curly-in-string --- Yup messages can be customised using template curlies in strings.
    .min(12, 'Password must contain at least ${min} characters')
    .matches(/^[\S]+.*[\S]+$/, 'Password cannot start or end with a space'),
  confirmNewPassword: Yup.string()
    .required('Both values for new password must match')
    .oneOf([Yup.ref('newPassword')], 'Both values for new password must match'),
});

export interface EnterNewPasswordFormProps {
  onSubmit: OnSubmit<ForgotPasswordSubmit>;
}

export const EnterNewPasswordForm: React.FC<EnterNewPasswordFormProps> = ({ onSubmit }) => (
  <Fade in>
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={newPasswordSchema}
        onSubmit={onSubmit}
        validateOnChange={false}
      >
        {({ errors, isSubmitting, touched }) => (
          <Form>
            <Box textAlign="center">
              <Typography>Enter a new password for your account.</Typography>
            </Box>
            <Box mt={3}>
              <Field name="newPassword">
                {({ field: { onBlur, onChange, name, value } }: FieldProps<ForgotPasswordSubmit['newPassword']>) => (
                  <TextField
                    autoFocus
                    disabled={isSubmitting}
                    error={!!touched.newPassword && !!errors.newPassword}
                    fullWidth
                    helperText={!!touched.newPassword && errors.newPassword}
                    id="password-field"
                    label="New password"
                    name={name}
                    onBlur={onBlur}
                    onChange={onChange}
                    size="medium"
                    type="password"
                    value={value}
                  />
                )}
              </Field>
            </Box>
            <Box mt={1}>
              <Field name="confirmNewPassword">
                {({
                  field: { onBlur, onChange, name, value },
                }: FieldProps<ForgotPasswordSubmit['confirmNewPassword']>) => (
                  <TextField
                    disabled={isSubmitting}
                    error={!!touched.confirmNewPassword && !!errors.confirmNewPassword}
                    fullWidth
                    helperText={!!touched.confirmNewPassword && errors.confirmNewPassword}
                    id="confirm-password-field"
                    label="Re-enter your new password"
                    name={name}
                    onBlur={onBlur}
                    onChange={onChange}
                    size="medium"
                    type="password"
                    value={value}
                  />
                )}
              </Field>
            </Box>
            <Box mt={3}>
              <Button color="primary" disabled={isSubmitting} fullWidth size="large" type="submit" variant="contained">
                {isSubmitting ? <CircularProgress size={26} color="inherit" /> : 'Change password'}
              </Button>
            </Box>
            <Box mt={2} textAlign="center">
              <RouterLink to="/auth/signin">Back to sign in screen</RouterLink>
            </Box>
          </Form>
        )}
      </Formik>
    </div>
  </Fade>
);
